<template>
  <div
    class="editor w-100"
    :class="{
      'editor--qaVariant': qaVariant,
      'editor--qaVariant--error': qaVariantError,
      'editor--withInsideFooter': Boolean($slots['inside-footer']),
    }"
  >
    <div
      v-if="isMenubarEnabled"
      class="editor__menubar"
      :class="{
        'editor__menubar--disabled': disabled,
        'editor__menubar--error': errorMessages.length,
        'editor__menubar--qaVariant': qaVariant,
      }"
    >
      <v-slide-x-transition>
        <div class="menubar d-flex align-center flex-wrap px-2">
          <div class="d-flex">
            <AppTooltip>
              <template #activator="{ props }">
                <app-button :class="{ 'format--active' : editor.isActive('bold')}"
                            :disabled="disabled"
                            type="icon"
                            size="small"
                            v-bind="props"
                            @click="toggleBold"
                >
                  <app-material-icon name="format_bold" color="grey-700" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.boldTooltip') }}</span>
            </AppTooltip>
            <AppTooltip>
              <template #activator="{ props }">
                <app-button :class="{ 'format--active' : editor.isActive('italic')}"
                            :disabled="disabled"
                            type="icon"
                            size="small"
                            v-bind="props"
                            @click="toggleItalic"
                >
                  <app-material-icon name="format_italic" color="grey-700" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.italicTooltip') }}</span>
            </AppTooltip>
            <AppTooltip>
              <template #activator="{ props }">
                <app-button :class="{ 'format--active' : editor.isActive('strike')}"
                            :disabled="disabled"
                            type="icon"
                            size="small"
                            v-bind="props"
                            @click="toggleStrike"
                >
                  <app-material-icon name="strikethrough_s" color="grey-700" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.strikeTooltip') }}</span>
            </AppTooltip>
            <AppTooltip>
              <template #activator="{ props }">
                <app-button :class="{ 'format--active' : editor.isActive('underline')}"
                            :disabled="disabled"
                            type="icon"
                            size="small"
                            v-bind="props"
                            @click="toggleUnderline"
                >
                  <app-material-icon name="format_underlined" color="grey-700" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.underlineTooltip') }}</span>
            </AppTooltip>

            <AppTooltip>
              <template #activator="{ props }">
                <app-button :class="{ 'format--active' : editor.isActive('highlight')}"
                            :disabled="disabled"
                            type="icon"
                            size="small"
                            v-bind="props"
                            @click="toggleHighlight"
                >
                  <app-material-icon name="format_ink_highlighter" color="grey-700" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.highlightTooltip') }}</span>
            </AppTooltip>

            <v-divider :vertical="true"
                       class="mx-2 my-auto"
                       color="black"
                       style="height: 20px"
            />

            <AppTooltip>
              <template #activator="{ props }">
                <app-button :class="{ 'format--active' : editor.isActive('bulletList')}"
                            :disabled="disabled"
                            type="icon"
                            size="small"
                            v-bind="props"
                            @click="toggleBulletList"
                >
                  <app-material-icon name="format_list_bulleted" color="grey-700" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.bulletListTooltip') }}</span>
            </AppTooltip>
            <AppTooltip>
              <template #activator="{ props }">
                <app-button :class="{ 'format--active' : editor.isActive('orderedList')}"
                            :disabled="disabled"
                            type="icon"
                            size="small"
                            v-bind="props"
                            @click="toggleOrderedList"
                >
                  <app-material-icon name="format_list_numbered" color="grey-700" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.orderedListTooltip') }}</span>
            </AppTooltip>
          </div>
          <div class="ml-auto">
            <TextCounter v-if="counter === 'menubar'"
                         :currentLength="textLength"
                         :maxLength="maxLength"
            />

            <AppTooltip v-if="sendButton">
              <template #activator="{ props }">
                <app-button type="icon"
                            size="small"
                            v-bind="props"
                            :disabled="textIsTooLong || !textLength || disabled"
                            @click="onSendClicked"
                >
                  <app-material-icon name="send" color="grey-700" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.sendTooltip') }}</span>
            </AppTooltip>
          </div>
        </div>
      </v-slide-x-transition>
    </div>

    <div class="editor__content-wrapper">
      <EditorContent ref="editor"
                     :editor="editor"
                     class="editor__content bg-white"
                     :class="{
                       'editor__content-oneLine': oneLine,
                       'editor__content--error': errorMessages.length,
                       'editor__content--qaVariant': qaVariant,
                     }"
      />

      <!-- Slot footer inside editor -->
      <div
        v-if="$slots['inside-footer']"
        class="editor__inside-footer"
      >
        <slot name="inside-footer" />
      </div>
    </div>

    <div
      v-if="errorMessages.length || counter === 'footer'"
      class="editor__footer"
      :class="{
        'editor__footer--qaVariant': qaVariant,
      }"
    >
      <div v-if="errorMessages" class="editor__errors">
        <div v-for="(errorMessage, index) in errorMessages"
             :key="index"
             class="text-error text-caption"
        >
          {{ errorMessage }}
        </div>
      </div>

      <TextCounter v-if="counter === 'footer'"
                   :currentLength="textLength"
                   :label="counterLabel"
                   :maxLength="maxLength"
                   class="editor__counter"
                   :class="{
                     'editor__counter--qaVariant': qaVariant,
                   }"
      />
    </div>
  </div>
</template>

<script>
import { Highlight } from '@tiptap/extension-highlight'
import Mention from '@tiptap/extension-mention'
import { Placeholder } from '@tiptap/extension-placeholder'
import { TextStyle } from '@tiptap/extension-text-style'
import { Underline } from '@tiptap/extension-underline'
import { StarterKit } from '@tiptap/starter-kit'
import { Editor, EditorContent, VueRenderer } from '@tiptap/vue-3'
import { cloneDeep } from 'lodash-es'
import tippy from 'tippy.js'

import AppTooltip from '@/common/AppTooltip.vue'
import MentionList from '@/common/mentions/MentionList.vue'
import TextCounter from '@/common/TextCounter.vue'
import { escapeHtml } from '@/common/utils/strings'
import { cleanHtml } from '@/common/helpers/htmlHelpers'

const MENTION_DELIMITER_KEYS = new Set([
  'Enter',
  'Tab',
  ' ',
  ',',
  ';',
  ':',
  '.',
  '!',
  '?',
  ')',
  ']',
  '}',
])

export default {
  name: 'VisualTextEditor',
  components: {
    AppTooltip,
    EditorContent,
    TextCounter,
  },
  props: {
    value: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
    placeholder: {
      type: String,
      default: '',
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    taggableMembers: {
      type: Array,
      default: () => [],
    },
    taggableGroups: {
      type: Array,
      default: () => [],
    },
    oneLine: {
      type: Boolean,
      default: false,
    },
    sendButton: {
      type: Boolean,
      default: false,
    },
    hideMenubar: {
      type: Boolean,
      default: false,
    },
    cleanContentOnValidation: {
      type: Boolean,
      default: false,
    },
    cleanContentOnBlur: {
      type: Boolean,
      default: true,
    },
    keepToolbarOnBlur: {
      type: Boolean,
      default: false,
    },
    maxLength: {
      type: Number,
      required: false,
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
    counter: {
      validator (value) {
        return ['menubar', 'footer'].includes(value)
      },
      required: false,
    },
    counterLabel: {
      type: String,
      default: '',
    },
    qaVariant: {
      type: Boolean,
      default: false,
    },
    cleanHtmlOnInput: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
    },
    cleanPasteStyle: {
      type: Boolean,
      default: false,
    },
    qaVariantError: {
      type: Boolean,
      default: false,
    },
    mentionListMaxVisibleItems: {
      type: Number,
      default: 5,
    },
    preventDuplicateMentions: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:value', 'input', 'blur', 'focus', 'sendClicked'],
  data () {
    return {
      editor: null,
      editorIsFocused: false,
      pickedHighlightColor: '#FFE066',
      items: [],
      tippyPopup: null,
      mentionSuggestionState: {
        active: false,
        query: '',
        range: null,
        items: [],
      },
    }
  },
  computed: {
    isEditable () {
      return !this.disabled && !this.readOnly
    },
    isMenubarEnabled () {
      return this.editor && !this.hideMenubar && !this.readOnly
    },
    selectedMentionIds () {
      const ids = new Set()
      const sourceHtml = this.editor?.getHTML?.() || this.value || ''
      if (!sourceHtml) {
        return ids
      }
      const regex = /data-id="([^"]+)"/g
      let match = regex.exec(sourceHtml)
      while (match !== null) {
        ids.add(match[1])
        match = regex.exec(sourceHtml)
      }
      return ids
    },
    mentionable () {
      const items = []
      if (this.taggableGroups.length) {
        const groups = cloneDeep(this.taggableGroups)
          .map(group => {
            const displayLabel = group.name || ''
            const normalizedName = this.normalizeMentionText(displayLabel)
            const collapsedName = this.normalizeMentionText(displayLabel, { collapseSpaces: true })
            const nameTokens = displayLabel
              .split(/\s+/)
              .map(token => this.normalizeMentionText(token))
              .filter(Boolean)

            return {
              ...group,
              mentionType: 'group',
              mentionId: `group-${group.id}`,
              searchableLabel: displayLabel,
              displayLabel,
              mentionSearchKeys: this.uniqueStrings([
                normalizedName,
                collapsedName,
                ...nameTokens,
              ]),
              mentionExactKeys: this.uniqueStrings([
                normalizedName,
                collapsedName,
              ]),
            }
          })
          .sort((a, b) => {
            return a.displayLabel.localeCompare(b.displayLabel, undefined, {
              numeric: true,
              sensitivity: 'base',
            })
          })

        const filteredGroups = this.preventDuplicateMentions
          ? groups.filter(group => !this.selectedMentionIds.has(group.mentionId))
          : groups

        items.push(...filteredGroups)
      }

      if (this.taggableMembers.length) {
        const members = cloneDeep(this.taggableMembers)
          .map(member => {
            const rawFullName = member.rawFullName || member.fullName || ''
            const fullNameTokens = rawFullName
              .split(/\s+/)
              .map(token => this.normalizeMentionText(token))
              .filter(Boolean)

            const normalizedFullName = this.normalizeMentionText(rawFullName)
            const collapsedFullName = this.normalizeMentionText(rawFullName, { collapseSpaces: true })

            return {
              ...member,
              mentionType: 'user',
              mentionId: `user-${member.id}`,
              searchableLabel: [member.fullName || '', member.groupLabel || ''].join(' ').trim(),
              displayLabel: member.fullName || '',
              groupLabel: member.groupLabel || '',
              initials: member.initials || member.shortInitials,
              mentionSearchKeys: this.uniqueStrings([
                normalizedFullName,
                collapsedFullName,
                ...fullNameTokens,
              ]),
              mentionExactKeys: this.uniqueStrings([
                normalizedFullName,
                collapsedFullName,
                ...fullNameTokens,
              ]),
            }
          })
          .sort((a, b) => {
            return a.displayLabel.localeCompare(b.displayLabel, undefined, {
              numeric: true,
              sensitivity: 'base',
            })
          })

        const filteredMembers = this.preventDuplicateMentions
          ? members.filter(member => !this.selectedMentionIds.has(member.mentionId))
          : members

        items.push(...filteredMembers)
      }
      return items
    },
    textLength () {
      return escapeHtml(this.value).length
    },
    textIsTooLong () {
      return this.maxLength && this.textLength > this.maxLength
    },
    formattedValue () {
      // Format mentions spans and add a space before the end of paragraph
      const formattedMention = this.value?.replaceAll('data-mention', 'data-type="mention"')
      if (formattedMention?.length) {
        const lastClosingTagIndex = formattedMention.lastIndexOf('</')
        if (lastClosingTagIndex !== -1) {
          return formattedMention.slice(0, lastClosingTagIndex) + ' ' + formattedMention.slice(lastClosingTagIndex)
        }
      }
      return formattedMention
    },
  },
  beforeUnmount () {
    this.destroyMentionPopup()
    this.editor?.destroy()
  },
  mounted () {
    this.editor = new Editor({
      extensions: [
        Mention.configure({
          HTMLAttributes: {
            class: 'mention',
          },
          suggestion: {
            char: '@',
            startOfLine: false,
            allowedPrefixes: null,
            allow: ({ state, range }) => this.isMentionAllowed(state, range),
            items: ({ query }) => this.getFilteredMentionItems(query),
            render: () => {
              let component = null
              let popup = null
              const createPopup = (props) => {
                if (!props.items?.length) return
                component = new VueRenderer(MentionList, {
                  props: {
                    ...props,
                    maxVisibleItems: this.mentionListMaxVisibleItems,
                  },
                  editor: this.editor,
                })
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                })[0]
                this.tippyPopup = popup
              }

              return {
                onStart: (props) => {
                  this.setMentionSuggestionState({
                    active: true,
                    ...props,
                  })
                  createPopup(props)
                },
                onUpdate: (props) => {
                  this.setMentionSuggestionState({
                    active: true,
                    ...props,
                  })
                  if (!props.items?.length) {
                    if (popup) {
                      popup.destroy()
                      popup = null
                      this.tippyPopup = null
                    }
                    if (component) {
                      component.destroy()
                      component = null
                    }
                    return
                  }
                  if (!component || !popup) {
                    createPopup(props)
                    return
                  }
                  component.updateProps({
                    ...props,
                    maxVisibleItems: this.mentionListMaxVisibleItems,
                  })
                  popup.setProps({
                    getReferenceClientRect: props.clientRect,
                  })
                },
                onKeyDown: (props) => {
                  if (props.event.key === 'Escape') {
                    popup?.hide()
                    return true
                  }
                  return component?.ref?.onKeyDown?.(props) || false
                },
                onExit: () => {
                  this.setMentionSuggestionState()
                  if (popup) {
                    popup.destroy()
                    popup = null
                  }
                  if (component) {
                    component.destroy()
                    component = null
                  }
                  this.tippyPopup = null
                },
              }
            },
          },
        }),
        StarterKit,
        Underline,
        TextStyle,
        Highlight,
        Placeholder.configure({
          placeholder: this.placeholder,
        }),
      ],
      editorProps: {
        handleKeyDown: (_, event) => {
          if (this.shouldResolveMentionOnKey(event)) {
            return this.tryResolvePendingMention({ appendText: event.key === ' ' ? ' ' : '' })
          }
          return false
        },
        handleDOMEvents: {
          drop: (_, e) => { e.preventDefault() },
          paste: (view, e) => {
            if (!this.cleanPasteStyle) {
              return false
            }
            e.preventDefault()
            const text = e.clipboardData?.getData('text/plain') ?? ''
            view.dispatch(view.state.tr.insertText(text))
            return true
          },
        },
      },
      editable: this.isEditable,
      parseOptions: {
        preserveWhitespace: 'full',
      },
      injectCSS: false,
      content: this.formattedValue,
      onUpdate: () => {
        const content = this.editor.getHTML()
        const finalContent = this.cleanHtmlOnInput ? cleanHtml(content) : content
        this.$emit('input', finalContent)
        this.$emit('update:value', finalContent)
      },
      onBlur: ({ event }) => {
        this.tryResolvePendingMention()

        if (event.relatedTarget) {
          if (!event.relatedTarget.classList.contains('blur-editor-exception') && !event.relatedTarget.classList.contains('tippy-box')) {
            this.destroyMentionPopup()
            if (this.cleanContentOnBlur || !this.keepToolbarOnBlur || this.value === '<p></p>') {
              this.editorIsFocused = false
            }
            this.$emit('blur')
            if (this.cleanContentOnValidation && this.cleanContentOnBlur) {
              this.editor.commands.clearContent()
            } else if (!this.keepToolbarOnBlur || this.value === '<p></p>') {
              this.editorIsFocused = false
            }
          }
        } else {
          this.$emit('blur')
          if (!this.keepToolbarOnBlur || this.value === '<p></p>') {
            this.editorIsFocused = false
          }
          if (this.cleanContentOnValidation && this.cleanContentOnBlur) {
            this.editor.commands.clearContent()
          }
          this.destroyMentionPopup()
        }
      },
      onFocus: () => {
        this.editorIsFocused = true
        this.$emit('focus')
      },
    })
    if (this.autofocus) {
      this.focus()
    }
  },
  methods: {
    toggleBold () { this.editor.chain().focus().toggleBold().run() },
    toggleItalic () { this.editor.chain().focus().toggleItalic().run() },
    toggleStrike () { this.editor.chain().focus().toggleStrike().run() },
    toggleUnderline () { this.editor.chain().focus().toggleUnderline().run() },
    toggleBulletList () { this.editor.chain().focus().toggleBulletList().run() },
    toggleOrderedList () { this.editor.chain().focus().toggleOrderedList().run() },
    clearFormat () { this.editor.chain().focus().clearNodes().unsetAllMarks().run() },
    toggleHighlight () { this.editor.chain().focus().toggleHighlight().run() },
    normalizeMentionText (value, { collapseSpaces = false } = {}) {
      if (!value) return ''
      return value
        .normalize('NFD')
        .replaceAll(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replaceAll(/[^\p{L}\p{N}\s]/gu, '')
        .trim()
        .replaceAll(/\s+/g, collapseSpaces ? '' : ' ')
    },
    uniqueStrings (values) {
      return Array.from(new Set((values || []).filter(Boolean)))
    },
    getFilteredMentionItems (query = '') {
      const normalizedQuery = this.normalizeMentionText(query, { collapseSpaces: true })
      if (!normalizedQuery) {
        return this.mentionable
      }
      return this.mentionable.filter(item => {
        const searchKeys = item.mentionSearchKeys || []
        return searchKeys.some(key => key.includes(normalizedQuery))
      })
    },
    isMentionAllowed (state, range) {
      const textBefore = state.doc.textBetween(Math.max(0, range.from - 1), range.from, '\n', '\0')
      const previousChar = textBefore.slice(-1)
      if (!previousChar) return true
      return !/[A-Za-z0-9._%+-]/.test(previousChar)
    },
    shouldResolveMentionOnKey (event) {
      if (!this.mentionSuggestionState?.active) return false
      if (event.isComposing) return false
      return MENTION_DELIMITER_KEYS.has(event.key)
    },
    setMentionSuggestionState (props = {}) {
      this.mentionSuggestionState = {
        active: props.active ?? false,
        query: props.query ?? '',
        range: props.range ?? null,
        items: props.items ?? [],
      }
    },
    destroyMentionPopup () {
      if (this.tippyPopup) {
        this.tippyPopup.destroy()
        this.tippyPopup = null
      }
      this.setMentionSuggestionState()
    },
    findExactMentionMatch (query) {
      const normalizedQuery = this.normalizeMentionText(query, { collapseSpaces: true })
      if (!normalizedQuery) return null

      const matches = this.mentionable.filter(item => {
        const exactKeys = item.mentionExactKeys || []
        return exactKeys.includes(normalizedQuery)
      })
      if (matches.length !== 1) {
        return null
      }
      const match = matches[0]
      if (this.preventDuplicateMentions && this.selectedMentionIds.has(match.mentionId)) {
        return null
      }
      return match
    },
    insertMentionAtRange (range, item, appendText = '') {
      if (!range || !item) return false
      this.editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: 'mention',
            attrs: {
              id: item.mentionId,
              label: item.rawFullName || item.fullName || item.name,
            },
          },
          ...(appendText ? [{ type: 'text', text: appendText }] : []),
        ])
        .run()
      this.destroyMentionPopup()
      return true
    },
    tryResolvePendingMention ({ appendText = '' } = {}) {
      const { active, query, range } = this.mentionSuggestionState || {}
      if (!active || !range) return false
      const exactMatch = this.findExactMentionMatch(query)
      if (!exactMatch) {
        return false
      }
      return this.insertMentionAtRange(range, exactMatch, appendText)
    },
    onOpenMentionList () {
      this.editor.commands.insertContent('@', { emitUpdate: true })
    },
    removeDuplicateMentions () {
      if (!this.preventDuplicateMentions) return
      const seen = new Set()
      const { state, view } = this.editor
      const { tr, doc } = state
      const positionsToDelete = []
      doc.descendants((node, pos) => {
        if (node.type.name === 'mention') {
          const mentionId = node.attrs.id
          if (seen.has(mentionId)) {
            positionsToDelete.push({ pos, size: node.nodeSize })
          } else {
            seen.add(mentionId)
          }
        }
      })
      if (!positionsToDelete.length) return
      // Delete in reverse order and map positions through the transaction
      // to account for position shifts caused by previous deletions
      positionsToDelete.reverse().forEach(({ pos, size }) => {
        const mappedFrom = tr.mapping.map(pos)
        const mappedTo = tr.mapping.map(pos + size)
        tr.delete(mappedFrom, mappedTo)
      })
      view.dispatch(tr)
    },
    onSendClicked () {
      this.tryResolvePendingMention()
      this.removeDuplicateMentions()
      this.$emit('sendClicked')
      if (this.cleanContentOnValidation) {
        this.editor.commands.clearContent()
        this.editorIsFocused = false
      }
    },
    focus () {
      this.editor.commands.focus('end')
    },
    clear () {
      this.editor.commands.clearContent()
    },
  },
  watch: {
    isEditable (newValue) {
      if (this.editor) {
        this.editor.setEditable(newValue)
      }
    },
    value (newValue) {
      if (this.editor && this.editor.getHTML() !== newValue) {
        this.editor.commands.setContent(newValue, false)
      }
    },
  },
}
</script>

<style lang="scss">
.editor {
  &--qaVariant {
    border: 1px solid rgb(var(--v-theme-grey-200));
    border-radius: 8px;

    &--error {
      border: 2px solid rgb(var(--v-theme-error));
    }
  }

  &__inside-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  &__menubar {
    padding-left: 10px;
    padding-right: 10px;
    background-color: rgb(var(--v-theme-grey-lighten-6));
    border: 1px solid rgb(var(--v-theme-grey-300));
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
    transition: opacity 0.1s ease-in-out;

    &--qaVariant {
      border: none;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
      padding: 0;

      .menubar {
        padding: 4px !important;

        & > div {
          gap: 4px;
        }

        hr {
          margin: auto 2px !important;
        }

        .app-button--icon {
          padding: 6px;
        }
      }
    }

    &-icon {
      font-size: 12px;
      color: rgb(var(--v-theme-accent-lighten-2));
    }

    &--disabled {
      opacity: 0.5;
    }

    &--error {
      border-top: 1px solid rgb(var(--v-theme-error));
      border-left: 1px solid rgb(var(--v-theme-error));
      border-right: 1px solid rgb(var(--v-theme-error));
    }
  }

  &__content {
    .ProseMirror {
      min-height: 75px;
      max-height: 400px;
      overflow-y: auto;
      padding: 12px;
      transition: all 0.2s;
      border: 1px solid #ddd;
      border-bottom-left-radius: 3px;
      border-bottom-right-radius: 3px;
      font-size: 14px;
      outline: none !important;
      &-focused {
        outline: none;
        border-color: rgb(var(--v-theme-tertiary-lighten-3));
      }
      .mention {
        color: rgb(var(--v-theme-info-darken-1));
      }
      * {
        white-space: pre-wrap;
        word-wrap: break-word;
      }

      ol, ul {
        padding-left: 16px;
      }

      color: rgb(var(--v-theme-text-secondary));
    }

    &-oneLine {
      .ProseMirror {
        min-height: 17px;
      }
    }

    &--qaVariant {
      .ProseMirror {
        border: none;
        height: 80px;
      }
    }

    &--error {
      .ProseMirror {
        border-bottom: 1px solid rgb(var(--v-theme-error));
        border-left: 1px solid rgb(var(--v-theme-error));
        border-right: 1px solid rgb(var(--v-theme-error));
      }
    }

    .is-read-only & {
      .ProseMirror {
        background-color: rgb(var(--v-theme-grey-50));
        border: 1px solid rgb(var(--v-theme-grey-200));
        color: rgb(var(--v-theme-grey-800)) !important;
      }
    }
  }

  &__footer {
    display: flex;
    justify-content: space-between;

    &--qaVariant {
      margin: 0 12px 12px 12px;
      height: 20px;
    }
  }

  &__errors {
    flex: 1;
  }

  &__counter {
    flex: 1;
    text-align: right;

    &--qaVariant {
      justify-content: flex-end;
      font-size: 14px !important;
    }
  }
}

.padding-prose-mirror .ProseMirror {
  padding: 10px;
}

.ProseMirror code {
  background-color: rgb(255, 255, 0);
  padding: 0px;
  font-size: 100%;
  color: rgb(var(--v-theme-accent));
  font-weight: initial;
  border-radius: 0px;
  font-family: 'Roboto', sans-serif;
}

.menubar {
  color: rgb(var(--v-theme-accent-lighten-4));

  .send-button:disabled {
    .editor__menubar-icon {
      color: rgb(var(--v-theme-accent-lighten-4));
    }

    .v-btn__overlay {
      opacity: 0;
    }
  }

  .format--active {
    background-color: rgb(var(--v-theme-grey-200)) !important;
  }
}

.ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: rgb(var(--v-theme-text-placeholder));
  font-size: 14px;
  pointer-events: none;
  height: 0;
}
</style>
